import React, { useState, useEffect } from 'react';
import { UserRole } from '../../types/auth';
import { apiService } from '../../services/apiService';

interface AvatarProps {
  name: string;
  role: UserRole;
  profilePhotoKey?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const Avatar: React.FC<AvatarProps> = ({
  name,
  role,
  profilePhotoKey,
  size = 'md',
  className = '',
}) => {
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const fetchPhotoUrl = async () => {
      if (profilePhotoKey) {
        try {
          const response = await apiService.get<{ url: string }>('/auth/profile/photo-url');
          setPhotoUrl(response.url);
          setImageError(false);
        } catch (error) {
          console.error('Failed to fetch profile photo URL:', error);
          setImageError(true);
        }
      } else {
        setPhotoUrl(null);
      }
    };

    fetchPhotoUrl();
  }, [profilePhotoKey]);

  const getUserInitials = (userName: string) => {
    return userName
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-16 h-16 text-xl',
  };

  const bgColorClass = role === UserRole.ADMINISTRATOR
    ? 'bg-purple-500'
    : 'bg-blue-500';

  const baseClasses = `rounded-full flex items-center justify-center text-white font-semibold overflow-hidden ${sizeClasses[size]} ${className}`;

  if (photoUrl && !imageError) {
    return (
      <div className={`${baseClasses} ${bgColorClass}`}>
        <img
          src={photoUrl}
          alt={`${name}'s profile`}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
      </div>
    );
  }

  return (
    <div className={`${baseClasses} ${bgColorClass}`}>
      {getUserInitials(name)}
    </div>
  );
};
